import {
  WORKFLOW_NODES,
  WORKFLOW_EDGES,
  WORKFLOW_NODES_BY_ID,
  TEST_LANES,
  PARKED_GROUP,
  NODE,
  NodeVariant,
  RowId,
  Slot,
  WorkflowNodeDef,
} from '../../workflow/workflowConfig';

// Pure geometry for the read-only workflow diagram. It turns the semantic grid placement in
// workflowConfig (col / row / slot / variant) into pixel rectangles that fill the measured container
// width, so the diagram always spans the full width at 100% zoom instead of being shrunk by fitView.
// Nothing here imports React, so it is unit-tested directly.

// Node sizes (px). Boxes sit on a row's flow line; chips hang below it in stacked tiers; count boxes
// are small label-less badges; the gate is a tall bar; the group is the Parked container.
export const BOX_W = 148;
export const BOX_H = 26;
// Chips are as wide as boxes so long side-state labels (e.g. "Changes requested") don't ellipsize.
export const CHIP_W = 148;
export const CHIP_H = 16;
export const COUNT_W = 46;
export const GROUP_W = 160;
export const GATE_W = 84;

// Vertical rhythm. Tuned so the whole diagram is ~240px tall (about 25% shorter than the old 320).
const TOP_MARGIN = 6;
const BOTTOM_MARGIN = 6;
const ROW_GAP = 4;    // gap between one row's bottom extent and the next row's top extent
const CHIP_GAP = 3;   // gap between a row's flow line and a chip hanging off it

// Horizontal rhythm. Columns have fixed widths; the gaps between them flex to fill the width.
const SIDE_MARGIN = 8;
const MIN_GAP = 28;

// The six columns, left to right: Parked group, Open/needs, Reviewing/testing, Changes requested/
// chips, Ready gate, Merged. Index === the `col` on each WorkflowNodeDef.
const COLUMN_WIDTHS = [GROUP_W, BOX_W, BOX_W, BOX_W, GATE_W, BOX_W];
const COLUMNS_WIDTH = COLUMN_WIDTHS.reduce((a, b) => a + b, 0);

// The narrowest container we lay out for; below this the viewport zooms out proportionally rather
// than letting nodes overlap.
export const MIN_WIDTH = 2 * SIDE_MARGIN + COLUMNS_WIDTH + MIN_GAP * (COLUMN_WIDTHS.length - 1);

// The Parked group's internal layout: a caption band above the stacked parked boxes, which are
// full-size boxes (BOX_W x BOX_H) so they are as easy to click as the lifecycle boxes. The panel is
// padded on all four sides so no child ever sits on the edge of the shaded area.
const GROUP_LABEL_H = 20;
const GROUP_PAD = 4;
// The foot of the panel needs more room than the sides: the caption band gives the first box plenty
// of air above it, so an equal 4px at the bottom left the last box looking crammed against the edge.
const GROUP_BOTTOM_PAD = 10;
const GROUP_ROW_GAP = 3;
const PARKED_COUNT = WORKFLOW_NODES.filter(n => n.parentId === PARKED_GROUP).length;
const GROUP_H =
  GROUP_LABEL_H + GROUP_PAD + PARKED_COUNT * BOX_H + (PARKED_COUNT - 1) * GROUP_ROW_GAP + GROUP_BOTTOM_PAD;

// The rows, top to bottom: the review line on top (with the reviewer overarching), then the unit
// line, then one line per scoped test lane — so all the testing channels sit together beneath review.
const ROW_ORDER: RowId[] = ['review', 'unit', ...TEST_LANES.map(l => l.id)];

// How many chip tiers hang below a row's flow line (0 if none) — the max slot among the row's nodes.
function rowDepth(row: RowId): number {
  let depth = 0;
  WORKFLOW_NODES.forEach(n => {
    if (!n.parentId && n.variant !== 'gate' && n.row === row && n.slot > depth) depth = n.slot;
  });
  return depth;
}

// Distance from a row's flow line up to its top extent — just half a box, since nothing hangs above.
function halfTop(): number {
  return BOX_H / 2;
}

// Distance from a row's flow line down to its bottom extent: half a box plus one CHIP_H per chip tier.
function halfBottom(row: RowId): number {
  return BOX_H / 2 + rowDepth(row) * (CHIP_GAP + CHIP_H);
}

// The y of each row's flow line, stacked with a gap between adjacent rows' extents.
function computeRowLineY(): { lineY: Record<RowId, number>; height: number } {
  const lineY: Record<RowId, number> = {};
  let y = TOP_MARGIN;
  ROW_ORDER.forEach((row, i) => {
    y += halfTop();
    lineY[row] = y;
    const isLast = i === ROW_ORDER.length - 1;
    y += halfBottom(row) + (isLast ? 0 : ROW_GAP);
  });
  return { lineY, height: y + BOTTOM_MARGIN };
}

const { lineY: ROW_LINE_Y, height: DIAGRAM_HEIGHT_RAW } = computeRowLineY();

// The fixed intrinsic height of the diagram at zoom 1.
export const DIAGRAM_HEIGHT = DIAGRAM_HEIGHT_RAW;

// The gate spans (almost) the full height so every lane can funnel straight into its left side.
const GATE_TOP = TOP_MARGIN + 2;
const GATE_BOTTOM = DIAGRAM_HEIGHT - BOTTOM_MARGIN - 2;
const GATE_H = GATE_BOTTOM - GATE_TOP;

function nodeSize(variant: NodeVariant): { w: number; h: number } {
  switch (variant) {
    case 'box': return { w: BOX_W, h: BOX_H };
    case 'chip': return { w: CHIP_W, h: CHIP_H };
    case 'count': return { w: COUNT_W, h: BOX_H };
    case 'gate': return { w: GATE_W, h: GATE_H };
    case 'group': return { w: GROUP_W, h: GROUP_H };
  }
}

// The center y of a node given its row and slot. Slot 0 sits on the flow line; each higher tier hangs
// a further CHIP_H below it, so a lane's side states stack straight down beneath its line.
function centerYForSlot(row: RowId, slot: Slot): number {
  const line = ROW_LINE_Y[row];
  if (slot <= 0) return line;
  return line + BOX_H / 2 + CHIP_GAP + CHIP_H / 2 + (slot - 1) * (CHIP_H + CHIP_GAP);
}

export interface GateInlet {
  id: string;      // matches the RF target handle id 'in-<sourceId>'
  offsetY: number; // y within the gate node
}

export interface LaidOutNode {
  id: string;
  def: WorkflowNodeDef;
  x: number;       // top-left; relative to the parent when parentId is set, absolute otherwise
  y: number;
  width: number;
  height: number;
  parentId?: string;
  // Gate-only: one left inlet per incoming lane. The right outlet offset feeds Merged on the gate and
  // Open on the Parked group, so each keeps its outgoing line straight.
  inlets?: GateInlet[];
  outletOffsetY?: number;
}

export interface WorkflowLayout {
  nodes: LaidOutNode[];
  width: number;   // the coordinate-space width nodes are laid out in (>= MIN_WIDTH)
  height: number;  // DIAGRAM_HEIGHT
  zoom: number;    // 1 at full width; < 1 when the container is narrower than MIN_WIDTH
}

// The center y of any node (used to align gate inlets so each lane's line is straight).
function nodeCenterY(def: WorkflowNodeDef): number {
  if (def.variant === 'gate') return GATE_TOP + GATE_H / 2;
  return centerYForSlot(def.row, def.slot);
}

// Lay the whole diagram out for a given container width. Fixed column widths, flexible gaps.
export function layoutWorkflow(containerWidth: number): WorkflowLayout {
  const zoom = containerWidth >= MIN_WIDTH ? 1 : containerWidth / MIN_WIDTH;
  const width = Math.max(containerWidth, MIN_WIDTH);

  const gap = (width - 2 * SIDE_MARGIN - COLUMNS_WIDTH) / (COLUMN_WIDTHS.length - 1);
  const colLeft: number[] = [];
  let x = SIDE_MARGIN;
  COLUMN_WIDTHS.forEach((w, i) => {
    colLeft[i] = x;
    x += w + gap;
  });

  // The gate's inlets: one per edge into Ready, aligned to that source's center y so the line is
  // horizontal. The outlet (to Merged) aligns to the review line.
  const inlets: GateInlet[] = WORKFLOW_EDGES.filter(e => e.target === NODE.ready).map(e => ({
    id: 'in-' + e.source,
    offsetY: nodeCenterY(WORKFLOW_NODES_BY_ID[e.source]) - GATE_TOP,
  }));
  // The outlet feeding Merged aligns to Merged's center (bottom-anchored below) so Ready -> Merged
  // runs straight along the diagram's bottom edge.
  const mergedCenterY = DIAGRAM_HEIGHT - BOTTOM_MARGIN - BOX_H / 2;
  const outletOffsetY = mergedCenterY - GATE_TOP;

  // Parked boxes are laid out inside the group (relative coordinates); everything else is absolute.
  let parkedIndex = 0;

  const nodes: LaidOutNode[] = WORKFLOW_NODES.map(def => {
    const size = nodeSize(def.variant);

    if (def.parentId === PARKED_GROUP) {
      const relX = (GROUP_W - size.w) / 2;
      const relY = GROUP_LABEL_H + GROUP_PAD + parkedIndex * (size.h + GROUP_ROW_GAP);
      parkedIndex += 1;
      return { id: def.id, def, x: relX, y: relY, width: size.w, height: size.h, parentId: PARKED_GROUP };
    }

    const columnLeft = colLeft[def.col];
    const columnWidth = COLUMN_WIDTHS[def.col];
    const nodeX = columnLeft + (columnWidth - size.w) / 2;

    if (def.variant === 'gate') {
      return {
        id: def.id, def, x: nodeX, y: GATE_TOP, width: size.w, height: size.h,
        inlets, outletOffsetY,
      };
    }

    if (def.variant === 'group') {
      // Top-left anchor: the diagram flows left-to-right, top-to-bottom, so Parked leads at the top.
      const groupY = TOP_MARGIN;
      // Straighten the Parked -> Open line by placing the group's right outlet at Open's flow line.
      return { id: def.id, def, x: nodeX, y: groupY, width: size.w, height: size.h, outletOffsetY: ROW_LINE_Y.review - groupY };
    }

    if (def.id === NODE.merged) {
      // Bottom-right anchor, mirroring Parked's top-left so the whole diagram reads top-to-bottom.
      const mergedY = DIAGRAM_HEIGHT - BOTTOM_MARGIN - size.h;
      return { id: def.id, def, x: nodeX, y: mergedY, width: size.w, height: size.h };
    }

    const centerY = centerYForSlot(def.row, def.slot);
    return { id: def.id, def, x: nodeX, y: centerY - size.h / 2, width: size.w, height: size.h };
  });

  return { nodes, width, height: DIAGRAM_HEIGHT, zoom };
}
